import React, { useState, useEffect } from "react";
import "./UserFormModal.css";
import { User, CreateUserData, UpdateUserData } from "../services/userService";

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserData | UpdateUserData) => Promise<void>;
  user?: User | null;
  mode: "create" | "edit";
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  mode,
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    is_active: true,
    is_staff: false,
    is_superuser: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode === "edit" && user) {
      setFormData({
        username: user.username,
        email: user.email,
        password: "",
        confirm_password: "",
        is_active: user.is_active,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
      });
    } else {
      setFormData({
        username: "",
        email: "",
        password: "",
        confirm_password: "",
        is_active: true,
        is_staff: false,
        is_superuser: false,
      });
    }
    setError("");
  }, [mode, user, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = (): boolean => {
    if (mode === "create") {
      if (!formData.username || !formData.email || !formData.password) {
        setError("Username, email, and password are required");
        return false;
      }
      if (formData.password !== formData.confirm_password) {
        setError("Passwords do not match");
        return false;
      }
      if (formData.password.length < 8) {
        setError("Password must be at least 8 characters");
        return false;
      }
    } else {
      if (!formData.username || !formData.email) {
        setError("Username and email are required");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === "create") {
        const createData: CreateUserData = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirm_password,
        };
        await onSubmit(createData);
      } else {
        const updateData: UpdateUserData = {
          username: formData.username,
          email: formData.email,
          is_active: formData.is_active,
          is_staff: formData.is_staff,
          is_superuser: formData.is_superuser,
        };
        await onSubmit(updateData);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{mode === "create" ? "Create New User" : "Edit User"}</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          {mode === "create" && (
            <>
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm_password">Confirm Password *</label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                  minLength={8}
                />
              </div>
            </>
          )}

          {mode === "edit" && (
            <div className="form-group-checkboxes">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="is_active">Active</label>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_staff"
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="is_staff">Staff</label>
              </div>

              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_superuser"
                  name="is_superuser"
                  checked={formData.is_superuser}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="is_superuser">Superuser</label>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? mode === "create"
                  ? "Creating..."
                  : "Updating..."
                : mode === "create"
                ? "Create User"
                : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
